import { CircularProgress } from '@mui/material';
import { FC, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCircleByJoinCode } from 'src/api/othRegistration';

import styles from './CircleRegistration.module.scss';
import {
  JOIN_BUTTON_LABEL,
  JOIN_CODE_LABEL,
  JOIN_CODE_LENGTH,
  JOIN_CODE_PLACEHOLDER,
  JOIN_FAILED_TEXT,
  JOIN_LOOKING_LABEL,
  JOIN_NOT_FOUND_TEXT,
  JOIN_TEXT,
  JOIN_TITLE,
  normalizeJoinCode,
} from './registrationText';

const isNotFound = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'response' in error &&
  (error as { response?: { status?: number } }).response?.status === 404;

export const registrationPath = (tribeId: string): string =>
  `/registrering/${encodeURIComponent(tribeId)}`;

/**
 * `/bli-med` and `/bli-med/:code`: the six-character code a leader shows at
 * the information meeting, resolved to the Circle's registration page.
 */
export const JoinCirclePage: FC = () => {
  const navigate = useNavigate();
  const { code: codeFromPath = '' } = useParams<{ code: string }>();
  const [code, setCode] = useState(normalizeJoinCode(codeFromPath));
  const [looking, setLooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookUp = async (value: string) => {
    setLooking(true);
    setError(null);
    try {
      const circle = await getCircleByJoinCode(value);
      navigate(registrationPath(circle.tribe_id), { replace: true });
    } catch (lookupError) {
      setError(
        isNotFound(lookupError) ? JOIN_NOT_FOUND_TEXT : JOIN_FAILED_TEXT
      );
      setLooking(false);
    }
  };

  useEffect(() => {
    const fromPath = normalizeJoinCode(codeFromPath);
    if (fromPath.length === JOIN_CODE_LENGTH) {
      lookUp(fromPath);
    }
    // Only when the address changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromPath]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (code.length === JOIN_CODE_LENGTH && !looking) {
      lookUp(code);
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.content}>
        <div className={styles.brand}>Dunbar148</div>
        <form className={styles.card} onSubmit={handleSubmit}>
          <h1 className={styles.title}>{JOIN_TITLE}</h1>
          <p className={styles.text}>{JOIN_TEXT}</p>
          <label className={styles.field}>
            <span className={styles.label}>{JOIN_CODE_LABEL}</span>
            <input
              className={styles.codeInput}
              value={code}
              placeholder={JOIN_CODE_PLACEHOLDER}
              autoComplete="off"
              autoCapitalize="characters"
              inputMode="text"
              maxLength={JOIN_CODE_LENGTH}
              disabled={looking}
              onChange={(event) =>
                setCode(normalizeJoinCode(event.target.value))
              }
            />
          </label>
          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={code.length !== JOIN_CODE_LENGTH || looking}
            >
              {looking ? JOIN_LOOKING_LABEL : JOIN_BUTTON_LABEL}
            </button>
            {looking ? <CircularProgress size={22} /> : null}
          </div>
        </form>
      </main>
    </div>
  );
};
